import * as React from 'react';
import { Plan, PlanPackageCriterion } from 'fs-api-client';
import { ErrorRenderer } from '../common/ErrorRenderer';
import { PlanPackageEdit } from './PlanPackageEdit';
import { BillingViewHeader } from './BillingViewHeader';
import { BillingPlanView } from './BillingPlanView';
import { BillingPlan, PlanPackage, TierProp } from './BillingPlanModel';
import { ConfirmationModal } from './BillingPlanConfirmModal';
import { Providers } from 'portal-shared-lib';

interface BillingViewProps {
    hasChanges: boolean;
    billingPlan: BillingPlan;
    currency: string;
    canEdit: boolean;
    totalPaid: number;

    billingForecast?: BillingForecast;
    errorMessage?: string;

    handleDisableTopBarStatus: () => void;
    onSaveClicked: () => void;
    onUndoChangesClicked: () => void;
    onAddPlanPackageClicked: () => void;
    onPlanPackStorageOwnerChange: (id: string, criterionIdx: number, newValue: string) => void;
    onPlanPackStorageProviderChange: (id: string, criterionIdx: number, storageProvider: string) => void;
    onPlanPackSubscriptionTypeChange: (planPackId: string, criterionIdx: number, subscriptionType: string) => void;
    onProviderAdded: (planPackId: string) => void;
    onProviderRemoved: (planPackId: string, criterionIdx: number) => void;
    onProviderChange: (planPackId: string, criterionIdx: number, newProvider: Providers) => void;
    onPlanPackageRemoved: (id: string) => void;
    onPlanTierRemoved: (planPackId: string, planDimension: Plan.DimensionEnum, planTierIdx: number) => void;
    onPlanTierModified: (
        newValue: string,
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tierIdx: number,
        tierProp: TierProp) => void;
    onPlanDimensionChanged: (
        oldDimension: Plan.DimensionEnum,
        newDimension: Plan.DimensionEnum,
        planPackId: string) => void;
    onPlanTierAdded: (planPackId: string, planDimension: Plan.DimensionEnum) => void;
    onPlanRemoved: (planPackId: string, planDimension: Plan.DimensionEnum) => void;
    onBillingPlanReverted: () => void;
    onSubscriptionTypeAdded: (planPackId: string, subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum) => void;
    onSubscriptionTypeRemoved: (planPackId: string, criterionIdx: number) => void;
    onStartEditing: () => void;
    onStopEditing: (applyChanges: boolean) => void;
}

interface BillingForecast {
    amountDue: number;
    amountDueForecast: number;
    currency: string;
    trialDays: number;
    start: number;
    end: number;
}

enum OperationType {
    Save = 'Save',
    Revert = 'Revert'
}

interface BillingViewState {
    workingBillingPlanCopy?: BillingPlan;
    isEditButtonClicked: boolean;
    selectedPlanIdx?: number;
    selectedPlanPackId?: string;
    isNewPackage: boolean;
    pendingConfirmation: OperationType | null;
}

export class BillingView extends React.Component<BillingViewProps, BillingViewState> {
    constructor(props: BillingViewProps) {
        super(props);
        this.state = {
            isEditButtonClicked: false,
            isNewPackage: false,
            pendingConfirmation: null
        };

        this.onEditClicked = this.onEditClicked.bind(this);
        this.onCancel = this.onCancel.bind(this);
        this.onApply = this.onApply.bind(this);
        this.onPlanPackageRemoved = this.onPlanPackageRemoved.bind(this);
        this.onPlanRemoved = this.onPlanRemoved.bind(this);
    }

    render() {
        return (
            <React.Fragment>
                <BillingViewHeader
                    billingForecast={this.props.billingForecast}
                    isCustomPricing={this.props.billingPlan.isCustomPricing}
                    totalPaid={this.props.totalPaid}
                />
                {!this.state.isEditButtonClicked && this.renderBillingPlan()}
                {this.state.isEditButtonClicked && this.renderPlanEditor()}
                {this.props.errorMessage && <ErrorRenderer errorMessage={this.props.errorMessage}/>}
            </React.Fragment>
        );
    }

    private onPlanRemoved(planPack: PlanPackage, plan: Plan) {
        this.props.onPlanRemoved(planPack.id, plan.dimension);
    }

    private renderBillingPlan(): JSX.Element {
        return (
            <React.Fragment>
                {this.props.canEdit &&
                <div className="row listItem" style={{borderBottom: 'none', justifyContent: 'space-between', alignItems: 'center', display: 'flex'}}>
                    <div>
                        <button onClick={() => this.props.onAddPlanPackageClicked()} type="button" className="btn light">Add plan</button>
                    </div>
                    <div>
                        {!this.hasUnsavedChanged() && this.props.billingPlan.isCustomPricing ?
                            <button onClick={() => this.onBillingPlanReverted()} type="button" className="btn warning">Reset to PayGo</button> : null}
                        {this.hasUnsavedChanged() &&
                        <div style={{display: 'flex'}}>
                            <button onClick={() => this.props.onUndoChangesClicked()} type="button" className="btn light">Cancel</button>
                            <button style={{marginLeft: '10px'}} onClick={() => this.onSaveClicked()} type="button" className="btn">Publish</button>
                        </div>}
                    </div>
                </div>}
                {this.hasUnsavedChanged() ? (
                <div className="row listItem" style={{display: 'flex', justifyContent: 'flex-end'}}>
                    <div>*unsaved changes will be lost if you leave the "billing tab"</div>
                </div>) : null}
                <BillingPlanView
                    billingPlan={this.props.billingPlan}
                    currency={this.props.currency}
                    onEditClicked={this.onEditClicked}
                    onDeleteClicked={this.onPlanRemoved}
                    canEdit={this.props.canEdit}
                />
                <ConfirmationModal
                    isModalShown={!!this.state.pendingConfirmation}
                    confirmationText={`Please confirm billing plan ${this.state.pendingConfirmation === OperationType.Save ? 'save' : 'reset'}`}
                    onClose={() => this.onModalClosed()}
                    onConfirm={() => this.onOperationConfirmed()}
                />
            </React.Fragment>
        );
    }

    private onSaveClicked() {
        if (!this.state.pendingConfirmation) {
            this.setState({pendingConfirmation: OperationType.Save});
        }
    }

    private onBillingPlanReverted() {
        if (!this.state.pendingConfirmation) {
            this.setState({pendingConfirmation: OperationType.Revert});
        }
    }

    private onOperationConfirmed() {
        if (!this.state.pendingConfirmation) {
            return;
        }

        this.onModalClosed();
        if (this.state.pendingConfirmation === OperationType.Revert) {
            this.props.onBillingPlanReverted();
        } else if (this.state.pendingConfirmation === OperationType.Save) {
            this.props.onSaveClicked();
        }
    }

    private onModalClosed() {
        this.setState({pendingConfirmation: null});
    }

    private onEditClicked(planPackage: PlanPackage, plan: Plan): void {
        this.openEditor(planPackage, plan);
    }

    private hasUnsavedChanged(): boolean {
        return this.props.hasChanges;
    }

    private getSelectedPlanPackage() {
        return this.props.billingPlan.planPackages.find(planPack => planPack.id === this.state.selectedPlanPackId);
    }

    private getSelectedPlan(planPackage: PlanPackage) {
        const plans = planPackage.plans;
        if (plans.length >= this.state.selectedPlanIdx!) {
            return plans[this.state.selectedPlanIdx!];
        }

        return null;
    }

    private renderPlanEditor(): JSX.Element | null {
        if (!this.state.isEditButtonClicked) {
            return null;
        }

        const selectedPlanPack = this.getSelectedPlanPackage();
        if (!selectedPlanPack) {
            return null;
        }

        const selectedPlan = this.getSelectedPlan(selectedPlanPack);
        if (!selectedPlan) {
            return null;
        }

        return (
            <PlanPackageEdit
                isDeleted={false}
                onPlanPackStorageOwnerChange={this.props.onPlanPackStorageOwnerChange}
                onPlanPackStorageProviderChange={this.props.onPlanPackStorageProviderChange}
                onPlanPackSubscriptionTypeChange={this.props.onPlanPackSubscriptionTypeChange}
                onProviderAdded={this.props.onProviderAdded}
                onProviderChange={this.props.onProviderChange}
                onProviderRemoved={this.props.onProviderRemoved}
                onPlanPackageRemoved={this.onPlanPackageRemoved}
                onPlanTierModified={this.props.onPlanTierModified}
                onPlanTierRemoved={this.props.onPlanTierRemoved}
                onPlanDimensionChanged={this.props.onPlanDimensionChanged}
                onPlanTierAdded={this.props.onPlanTierAdded}
                onSubscriptionTypeAdded={this.props.onSubscriptionTypeAdded}
                onSubscriptionTypeRemoved={this.props.onSubscriptionTypeRemoved}
                planPackage={selectedPlanPack}
                selectedPlan={selectedPlan}
                onCancel={this.onCancel}
                onApply={this.onApply}
                isNewlyCreated={this.state.isNewPackage}
            />
        );
    }

    private onCancel() {
        this.closeEditor(false);
    }

    private onApply() {
        this.closeEditor(true);
    }

    private onPlanPackageRemoved(planPackId: string) {
        this.props.onPlanPackageRemoved(planPackId);
        this.closeEditor(true);
    }

    private openEditor(planPackage: PlanPackage | undefined, selectedPlan: Plan, isNewPackage: boolean = false): void {
        if (!planPackage) { return; }
        const planIdx = planPackage.plans.findIndex(plan => plan.dimension === selectedPlan.dimension);

        this.setState({
            isEditButtonClicked: true,
            selectedPlanPackId: planPackage.id,
            selectedPlanIdx: planIdx,
            isNewPackage
        });
        this.props.handleDisableTopBarStatus();
        this.props.onStartEditing();
    }

    private closeEditor(applyChanges: boolean): void {
        this.setState({
            isEditButtonClicked: false,
            selectedPlanPackId: undefined,
            selectedPlanIdx: undefined,
            isNewPackage: false
        });
        this.props.onStopEditing(applyChanges);
        this.props.handleDisableTopBarStatus();
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/billing/BillingView.tsx